/**
 * @fileoverview Custom autocomplete component that fetches options from an API or uses provided options.
 * Supports both single and multiple selection with dynamic data loading.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
    TextField,
    CircularProgress,
    Chip,
    Autocomplete,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { intl, fetchData } from "dmsbasereact";

const defaultFilter = createFilterOptions();

/**
 * DmsAutocomplete component for rendering a Material UI Autocomplete with dynamic options
 * @param {Object} props - Component props
 * @param {string|number|string[]|number[]} [props.value=[]] - Selected value(s)
 * @param {Object} [props.properties=null] - Object defining label and value field mapping
 * @param {string} [props.properties.label] - Path to label property in fetched data
 * @param {string} [props.properties.value] - Path to value property in fetched data
 * @param {boolean} [props.multiple=false] - Allow multiple selection
 * @param {boolean} [props.needs_authentication=true] - Whether to authenticate fetch
 * @param {string} [props.url] - Endpoint for fetching options
 * @param {Array<Object>|Object} [props.options] - Options (array or object)
 * @param {string} [props.name] - Field name
 * @param {string} [props.label] - Input label
 * @param {Function} [props.onChange] - Callback when value changes (receives value only)
 * @param {Function} [props.onOptionChange] - Callback when option changes (receives full option with label and value)
 * @returns {JSX.Element} The autocomplete input element with hidden field for form submission
 */

export const DmsAutocomplete = ({
    value = [],
    properties = null,
    multiple = false,
    needs_authentication = true,
    url,
    options,
    name,
    label,
    onChange,
    onOptionChange,
    onSearchChange,
    error: externalError,
    helperText: externalHelperText,
    required = false,
    disabled = false,
    size,
    fullWidth = true,
    loading: externalLoading = false,
    minInputLength = 0,
    sx,
    ...props
}) => {
    const [urlItems, setUrlItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [selectedValue, setSelectedValue] = useState(value);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const prevSelectedRef = useRef(null);

    // External error/helperText takes priority over internal
    const error = externalError || internalError;
    const helperText = externalHelperText || internalError;
    const isLoading = loading || externalLoading;
    const isBelowMinLength = minInputLength > 0 && inputValue.length < minInputLength;

    const getNestedValue = (obj, path) => {
        return path?.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    // For url-based options: fetch once when url changes
    useEffect(() => {
        if (!url) return;
        setLoading(true);
        setInternalError(null);
        fetchData(url, "GET", needs_authentication)
            .then((response) => {
                const data = response.data.data.rows || response.data.data;
                if (Array.isArray(data)) {
                    setUrlItems(
                        data.map((item) => ({
                            label: getNestedValue(item, properties?.label) || item.name,
                            value: getNestedValue(item, properties?.value) || item.pk,
                        }))
                    );
                } else {
                    throw new Error("Data format is incorrect.");
                }
            })
            .catch((err) => {
                setInternalError("Failed to load data.");
                console.error("Error fetching data:", err);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    // For prop-based options: compute synchronously so MUI always sees the latest list
    // without an extra render cycle from setState.
    const items = useMemo(() => {
        if (url) return urlItems;
        if (!options) return [];
        return Array.isArray(options)
            ? options
            : Object.entries(options).map(([key, val]) => ({
                label: val,
                value: key,
            }));
    }, [url, urlItems, options]);

    // Sync internal state with external value prop
    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    const handleChange = (event, newValue) => {
        let finalValue;
        if (multiple) {
            finalValue = newValue.map((opt) => opt.value);
        } else {
            finalValue = newValue?.value || "";
        }

        setSelectedValue(finalValue);
        if (onChange) onChange(finalValue);
        if (onOptionChange) onOptionChange(newValue);
    };

    // Stabilize selectedOption reference: when the same option is selected but items
    // array was replaced (new reference, same content), return the previous object so
    // MUI's value prop doesn't change reference and doesn't reset the inputValue/filter.
    const selectedOption = useMemo(() => {
        if (multiple) {
            return items.filter((opt) => selectedValue.includes(opt.value));
        }
        const found = items.find((opt) => opt.value === selectedValue) || null;
        const prev = prevSelectedRef.current;
        if (
            found !== null &&
            prev !== null &&
            found.value === prev.value &&
            found.label === prev.label
        ) {
            return prev;
        }
        prevSelectedRef.current = found;
        return found;
    }, [items, selectedValue, multiple]);

    return (
        <>
            <Autocomplete
                multiple={multiple}
                options={items}
                value={selectedOption}
                getOptionLabel={(option) => option.label || ""}
                loading={isLoading}
                noOptionsText={
                    isBelowMinLength
                        ? intl.get("NoOptionsMinChars").d(`Type at least ${minInputLength} characters`).replace("{0}", minInputLength)
                        : intl.get("NoOptions").d("No options")
                }
                loadingText={intl.get("Loading").d("Loading...")}
                onChange={handleChange}
                {...(onSearchChange ? { inputValue } : {})}
                onInputChange={(_, newInputValue, reason) => {
                    setInputValue(newInputValue);
                    if (onSearchChange && reason === "input") onSearchChange(newInputValue);
                }}
                filterOptions={(options, state) => {
                    if (isBelowMinLength) return [];
                    if (onSearchChange) return options;
                    return defaultFilter(options, state);
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                disabled={disabled}
                size={size}
                fullWidth={fullWidth}
                sx={sx}
                renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                        {option.label}
                    </li>
                )}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            label={option.label}
                            {...getTagProps({ index })}
                            key={option.value}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        name={name ? name + "_label" : undefined}
                        fullWidth={fullWidth}
                        variant="outlined"
                        required={required}
                        error={!!error}
                        helperText={helperText}
                        size={size}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                {...props}
            />

            <input type="hidden" name={name} value={selectedValue} />
        </>
    );
};

DmsAutocomplete.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        ),
    ]),
    properties: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    }),
    multiple: PropTypes.bool,
    needs_authentication: PropTypes.bool,
    url: PropTypes.string,
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
        PropTypes.objectOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        ),
    ]),
    name: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    onOptionChange: PropTypes.func,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(["small", "medium"]),
    fullWidth: PropTypes.bool,
    loading: PropTypes.bool,
    minInputLength: PropTypes.number,
    onSearchChange: PropTypes.func,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
